/**
 * Site-picker list logic for the Header "Switch Site" modal and the /select-site page.
 *
 * Keep this file IMPORT-FREE: cypress/node-tests/site-switch.test.js executes it directly.
 *
 * The list comes from POST /switchable-sites (main-service, 2026-09-17). For a Teamio operator it
 * holds every active site; a site without the operator's employee row is a stub with
 * `needsAccessRow: true` and NO EmployeeID. Picking any entry goes through POST /select-site
 * { returnUser: true }, which creates the row if needed and returns it — only THAT row may become
 * the session user (isSelectableUser).
 */

/** Server list when it answered with sites, else the cached login list; always a fresh array. */
export function pickSiteList(serverSites, cachedSites) {
  const source = Array.isArray(serverSites) && serverSites.length > 0
    ? serverSites
    : (Array.isArray(cachedSites) ? cachedSites : []);
  return source.map((site) => ({ ...site, SiteName: site.SiteName || site.Name }));
}

/** Put the row select-site returned into the cached list, replacing that site's entry (stub or old row). */
export function upsertSiteRow(cachedSites, userRow) {
  const list = Array.isArray(cachedSites) ? cachedSites.slice() : [];
  const i = list.findIndex((site) => site && site.SiteID === userRow.SiteID);
  if (i > -1) list[i] = userRow;
  else list.push(userRow);
  return list;
}

/** Case-insensitive match on practice or customer name. */
export function filterSites(sites, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return sites;
  return sites.filter((site) => [site.SiteName, site.Name, site.CustomerName]
    .some((v) => typeof v === 'string' && v.toLowerCase().includes(q)));
}

/** A row the app can log into: a real employee row, never a needsAccessRow stub. */
export function isSelectableUser(row) {
  return !!(row && !row.needsAccessRow && row.EmployeeID && row.SiteID && row.FirebaseUID);
}

/**
 * Who to log in as after POST /select-site. The server's row wins. If the call failed, a picked
 * entry that is itself a real row is still entered (the L3 rule, 2026-09-16: a backend hiccup never
 * blocks site entry). A stub has no account to enter without the server → null (the caller shows
 * `error`).
 */
export function resolveSwitchUser(result, picked) {
  if (result && isSelectableUser(result.user)) return result.user;
  if (isSelectableUser(picked)) return picked;
  return null;
}

/** The server's refusal text (select-site answers a JSON string), else null. */
export function switchErrorMessage(result) {
  const data = result && result.error && result.error.response && result.error.response.data;
  return typeof data === 'string' && data.trim() ? data : null;
}
